import { useState, useCallback, useEffect, ChangeEvent, ReactElement } from "react";

import { Box, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import {
  formatCurrencyAmount,
  OrganizationCountry,
  getCurrencyFromCountry,
  Emptyable,
  IsoLocale,
  DiscountType,
  DiscountScope,
  getDollarValue,
  Nullable,
  TicketingFormCategory,
  doesDiscountApplyToTickets,
  doesTicketCountExceedRemainingUses,
  PurchasedTicket,
} from "@simplyk/common";
import { FieldValues, Path, UseFormRegister, UseFormSetValue } from "react-hook-form";

import { DiscountOutput, DiscountWithRateMappings } from "../../../common/types/trpc";

import { ValidDiscountCodeBlock } from "./ValidDiscountCodeBlock";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useTranslate } from "@/common/hooks/useTranslate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormTextField } from "@/forms-design-system/FormTextField/FormTextField";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { Success as CheckCircle } from "@/icons/outlined";
export type { PurchasedTicket };

export interface DiscountCodeFieldProps<T extends FieldValues> {
  setValue: UseFormSetValue<T>;
  register: UseFormRegister<T>;
  discountIdName: Path<T>;
  ticketingId: string;
  organizationCountry: OrganizationCountry;
  discount?: DiscountOutput;
  selectedOccurrenceId: Nullable<string>;
  formCategory?: Nullable<TicketingFormCategory>;
  purchasedTickets: PurchasedTicket[] | undefined;
  validDiscount?: DiscountWithRateMappings | null;
  onValidDiscountChange?: (discount: DiscountWithRateMappings | null) => void;
}

enum DiscountValidation {
  "Valid" = "Valid",
  "Invalid" = "Invalid",
  "UnCheck" = "UnCheck",
}

export function DiscountCodeField<T extends FieldValues>({
  setValue,
  register,
  discountIdName,
  ticketingId,
  organizationCountry,
  discount,
  selectedOccurrenceId,
  formCategory,
  purchasedTickets = [],
  validDiscount = null,
  onValidDiscountChange,
}: React.PropsWithChildren<DiscountCodeFieldProps<T>>): ReactElement<
  React.PropsWithChildren<DiscountCodeFieldProps<T>>
> {
  const { t } = useTranslate();
  const theme = useTheme();
  const isFormV2 = theme.constants.isFormV2;
  const { isPreview } = usePreviewContext();

  const { isoLocale } = useLocaleContext();
  const { logAmplitudeEvent } = useAmplitude();
  const [codeIsValid, setCodeIsValid] = useState<DiscountValidation>(DiscountValidation.UnCheck);
  const [codeIsLoading, setCodeIsLoading] = useState(false);
  const setValidDiscount = useCallback(
    (discount: DiscountWithRateMappings | null) => onValidDiscountChange?.(discount),
    [onValidDiscountChange]
  );
  const { mutateAsync: execCheckDiscountCodeConformity } = trpc.getDiscountCodeFromTicketingIdAndBody.useMutation();
  const [discountCodeBody, setDiscountCodeBody] = useState<string>(discount?.body || "");
  const currentDiscount = discount || validDiscount;

  const validateCode = useCallback(async () => {
    setCodeIsLoading(true);
    logAmplitudeEvent(AmplitudeEvents.DonorFormDiscountCodeChecked, {
      formId: ticketingId,
      formType: formCategory,
      organizationCountry,
    });

    const discountCodeValidated = await execCheckDiscountCodeConformity({
      ticketingId,
      discountCodeBody,
      occurrenceId: selectedOccurrenceId,
    });

    const discountCode = discountCodeValidated.data;

    if (!discountCode) {
      setCodeIsValid(DiscountValidation.Invalid);
    } else {
      const isApplicable = doesDiscountApplyToTickets(discountCode, purchasedTickets);
      const exceedsLimit = doesTicketCountExceedRemainingUses(discountCode, purchasedTickets);
      const hasUsesRemaining = discountCode.remainingUses === null || discountCode.remainingUses > 0;

      if (!isApplicable || exceedsLimit || !hasUsesRemaining) {
        setCodeIsValid(DiscountValidation.Invalid);
      } else {
        setCodeIsValid(DiscountValidation.Valid);
        setValidDiscount({ ...discountCode, scope: discountCode.scope ?? DiscountScope.PerTicketAllRates });
        setValue(discountIdName, discountCode.id as never);
      }
    }
    setCodeIsLoading(false);
  }, [
    logAmplitudeEvent,
    ticketingId,
    formCategory,
    organizationCountry,
    execCheckDiscountCodeConformity,
    discountCodeBody,
    selectedOccurrenceId,
    setValidDiscount,
    setValue,
    discountIdName,
    purchasedTickets,
  ]);

  useEffect(() => {
    if (discountCodeBody) {
      setCodeIsValid(DiscountValidation.UnCheck);
    }
  }, [discountCodeBody]);

  useEffect(() => {
    if (selectedOccurrenceId) {
      setCodeIsValid(DiscountValidation.UnCheck);
      setValidDiscount(null);
      setValue(discountIdName, undefined as never);
      setDiscountCodeBody("");
    }
  }, [selectedOccurrenceId, setCodeIsValid, setValidDiscount, setValue, setDiscountCodeBody, discountIdName]);

  // Invalidate discount when purchased tickets change
  useEffect(() => {
    if (codeIsValid === DiscountValidation.Valid && validDiscount) {
      const isApplicable = doesDiscountApplyToTickets(validDiscount, purchasedTickets);
      const exceedsLimit = doesTicketCountExceedRemainingUses(validDiscount, purchasedTickets);

      if (!isApplicable || exceedsLimit) {
        setCodeIsValid(DiscountValidation.UnCheck);
        setValidDiscount(null);
        setValue(discountIdName, undefined as never);
      }
    }
  }, [purchasedTickets, codeIsValid, validDiscount, setValue, discountIdName, setValidDiscount]);

  const inputRequiredValidation = discountCodeBody && codeIsValid !== DiscountValidation.Valid;

  const deleteDiscountCode = useCallback(() => {
    setCodeIsValid(DiscountValidation.UnCheck);
    setValidDiscount(null);
    setValue(discountIdName, undefined as never);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDiscountCodeBodyChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setDiscountCodeBody(event.target.value),
    []
  );

  if (codeIsValid === DiscountValidation.Valid) {
    if (isFormV2) {
      return (
        <ValidDiscountCodeBlock
          register={register}
          name={discountIdName}
          currency={getCurrencyFromCountry(organizationCountry)}
          discountType={currentDiscount?.type}
          discountScope={currentDiscount?.scope}
          discountCode={currentDiscount?.body}
          discountAmount={currentDiscount?.amount}
          itemType={getItemType(formCategory)}
          onRemoveClick={deleteDiscountCode}
        />
      );
    }

    return (
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={(theme) => ({
          backgroundColor: theme.palette.success.lightest,
          borderRadius: theme.radius(0.5),
          padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
          minHeight: 45,
          display: "flex",
        })}
      >
        <input type="hidden" {...register(discountIdName)} />
        <Grid item>
          <Grid container>
            <DashboardIcon vibe="inherit" sx={{ mr: 1 }}>
              <CheckCircle />
            </DashboardIcon>
            <DashboardTypography variant="subtitle2" sx={{ mr: 0.5 }}>
              {t("ticketing", "validDiscountCode")}
            </DashboardTypography>
            <DashboardTypography variant="body2">
              {t("ticketing", "validDiscountCode.body", {
                body: currentDiscount?.body,
                value: getValidDiscountLabel(
                  currentDiscount,
                  organizationCountry,
                  isoLocale,
                  t("ticketing", "discountCodeField.valid.defaultLabel")
                ),
              })}
            </DashboardTypography>
          </Grid>
        </Grid>
        <Grid item>
          <DashboardButton vibe="positive" size="small" variant="text" onClick={deleteDiscountCode}>
            {t("common", "delete")}
          </DashboardButton>
        </Grid>
      </Grid>
    );
  }

  return (
    <Box>
      <input type="hidden" {...register(discountIdName)} data-test="discount-code-input" />
      <Stack direction="row" alignItems="flex-end" gap={1}>
        <FormTextField
          value={discountCodeBody}
          onChange={onDiscountCodeBodyChange}
          error={Boolean(inputRequiredValidation && codeIsValid !== DiscountValidation.UnCheck)}
          placeholder={!isPreview ? t("common", "discountCodeField.input.placeholder") : ""}
          label={isFormV2 ? t("ticketing", "discountCode") : ""}
          inputProps={{
            "aria-label": isFormV2 ? undefined : t("ticketing", "discountCode"),
            "aria-describedby":
              codeIsValid === DiscountValidation.Invalid && isFormV2 ? "discount-code-error" : undefined,
          }}
          disableInjectingGlobalStyles={isPreview}
          id="discount-code-field"
        />
        <Box
          sx={{
            width: "96px",
            marginBlock: "1px", // For visual alignment with the input field
          }}
        >
          {codeIsValid === DiscountValidation.UnCheck && (
            <FormButton
              onClick={validateCode}
              fullWidth
              disabled={!discountCodeBody || purchasedTickets.length === 0}
              isLoading={codeIsLoading}
              data-test="discount-code-validate-button"
            >
              {t("ticketing", "validateDiscountCode")}
            </FormButton>
          )}
          {codeIsValid === DiscountValidation.Invalid && (
            <FormButton
              variant="outline"
              fullWidth
              onClick={validateCode}
              disabled
              isLoading={codeIsLoading}
              data-test="discount-code-validate-button"
              sx={(theme) => ({
                color: theme.palette.forms.text.error,
                backgroundColor: "transparent",
                borderColor: theme.palette.forms.border.error,
              })}
            >
              {t("ticketing", "invalid")}
            </FormButton>
          )}
        </Box>
      </Stack>
      {codeIsValid === DiscountValidation.Invalid && isFormV2 && (
        <FormTypography id="discount-code-error" variant="caption" vibe="error" role="alert">
          {t("ticketing", "invalidDiscountCode")}
        </FormTypography>
      )}
    </Box>
  );
}

const getItemType = (formCategory: Emptyable<TicketingFormCategory>): "ticket" | "item" | "membership" => {
  switch (formCategory) {
    case TicketingFormCategory.MembershipV2:
      return "membership";
    case TicketingFormCategory.Shop:
    case TicketingFormCategory.Custom:
    case TicketingFormCategory.Auction:
      return "item";
    default:
      return "ticket";
  }
};

const getValidDiscountLabel = (
  discount: Emptyable<DiscountOutput>,
  country: OrganizationCountry,
  locale: IsoLocale,
  defaultMessage: string
): string => {
  if (!discount) {
    return defaultMessage;
  }
  return discount?.type === DiscountType.Dollars
    ? formatCurrencyAmount({
        value: getDollarValue(discount.amount),
        locale,
        currency: getCurrencyFromCountry(country),
      })
    : `${discount?.amount}%`;
};
